import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  AlertCircle,
  BellRing,
  CalendarClock,
  CalendarDays,
  CalendarRange,
  CircleDot,
  ClipboardList,
  Plus,
  Repeat,
  RotateCcw,
  Save,
  Sun,
  Trash2,
} from 'lucide-react'
import { api, extractApiError } from '../../lib/api'
import { formatPattern, uses24HourClock } from '../../lib/format'
import { Breadcrumb } from '../../components/Breadcrumb'
import { reportSchedule } from '../../lib/types'
import { Link } from 'react-router-dom'
import { useAuthStore } from '../../stores/authStore'
import { preferencesPathFor } from '../../lib/landing'
import { CompanyTaskRemindersEditor } from '../../components/CompanyTaskReminders'
import { nameSchema, emailSchema } from '../../lib/validators'

const WEEKDAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
]
const WEEKDAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

// Schedule-card icon chips — each cadence (daily / weekly / monthly)
// carries a distinct hue so the row reads with rhythm.
const TONE_TILE = {
  gold: 'bg-[#FEF3C7] text-[#D97706] border-[#FDE68A] dark:bg-white/[0.06] dark:text-amber-300 dark:border-white/10',
  sky: 'bg-[#DBEAFE] text-[#2563EB] border-[#BFDBFE] dark:bg-white/[0.06] dark:text-sky-300 dark:border-white/10',
  emerald: 'bg-[#D1FAE5] text-[#059669] border-[#A7F3D0] dark:bg-white/[0.06] dark:text-emerald-300 dark:border-white/10',
}

// The schedule splits into two INDEPENDENTLY-saved groups, each with its own
// Save Changes button: the report reminders (daily / weekly / monthly) and the
// Daily Task Assignment Digest. Each save sends only its own fields and leaves
// the other group's unsaved edits untouched, so these pick helpers scope both
// the dirty-check and the save payload.
const pickReminders = (s) => ({
  dailyEnabled: s.dailyEnabled,
  dailyReminderAt: s.dailyReminderAt,
  dailyActiveDays: s.dailyActiveDays,
  weeklyEnabled: s.weeklyEnabled,
  weeklyDayOfWeek: s.weeklyDayOfWeek,
  weeklyReminderAt: s.weeklyReminderAt,
  monthlyEnabled: s.monthlyEnabled,
  monthlyDayOfMonth: s.monthlyDayOfMonth,
  monthlyReminderAt: s.monthlyReminderAt,
  monthlyScope: s.monthlyScope,
  dailyGraceEnabled: s.dailyGraceEnabled,
  weeklyAdminDigestEnabled: s.weeklyAdminDigestEnabled,
  monthlyAdminDigestEnabled: s.monthlyAdminDigestEnabled,
})
const pickDigest = (s) => ({
  taskDigestEnabled: s.taskDigestEnabled,
  taskDigestReminderAt: s.taskDigestReminderAt,
  taskDigestReminderTimes: s.taskDigestReminderTimes,
  taskDigestRecipients: s.taskDigestRecipients,
})
const pickRepeat = (s) => ({
  repeatReportEnabled: s.repeatReportEnabled,
  repeatReportDayOfWeek: s.repeatReportDayOfWeek,
  repeatReportReminderAt: s.repeatReportReminderAt,
  repeatReportRecipients: s.repeatReportRecipients,
})
// Repeat Task Creation Time — the single HH:MM field controlling WHEN the
// recurring-task runner GENERATES occurrences (interpreted in the company
// timezone). Its own independently-saved group so it never clobbers the
// reminder / digest / repeat-report edits.
const pickCreation = (s) => ({
  repeatTaskCreationAt: s.repeatTaskCreationAt,
})
// Default Task Notification Time — the single HH:MM field controlling the
// FALLBACK time a task's assignment email is sent when the task has a start date
// but no specific start time (company timezone). Its own independently-saved
// group, and DELIBERATELY SEPARATE from the Repeat Task Creation Time above.
const pickNotification = (s) => ({
  defaultTaskNotificationAt: s.defaultTaskNotificationAt,
})

/** Company Admin "Reminders & Schedule" — controls when and how often the
 *  platform reminds users to submit reports. The actual cron is
 *  enforced server-side; this page only edits the per-tenant config. */
export function ReportSchedulePage() {
  const qc = useQueryClient()
  // Tenant timezone — the reminders worker interprets all picker times
  // (`HH:MM`) as wall-clock in THIS zone, not the browser's local
  // timezone. Surfacing it on every TimeField below is the cheapest fix
  // for the "email arrives at the wrong hour" complaint: as long as the
  // admin sees `Asia/Kolkata` (or whatever) under each field, they can
  // either dial in a time that matches OR fix the company timezone in
  // My Preferences → Region & formats.
  const tenantTimezone = useAuthStore((s) => s.tenant?.timezone) || null
  const scheduleQuery = useQuery({
    queryKey: ['report-schedule'],
    queryFn: async () => {
      const res = await api.get('/reports/config/schedule')
      return reportSchedule.parse(res.data)
    },
  })

  const [draft, setDraft] = useState(null)
  // Reveal the per-row "required" errors on the Additional Recipients only
  // after a save attempt, so a half-typed new row doesn't nag mid-entry.
  const [showRecipientErrors, setShowRecipientErrors] = useState(false)
  const [showRepeatRecipientErrors, setShowRepeatRecipientErrors] = useState(false)

  // Prime the editable draft ONCE, on first load. Seeding only when the draft
  // is still null is critical now that the two groups save independently: each
  // save updates the query cache (the baseline) for its OWN fields, and if this
  // effect re-seeded on every cache change it would clobber the other group's
  // unsaved edits. Loads the saved config verbatim — it must NOT mutate the
  // monthly fields here (the fixed "last day"/"current month" values are pinned
  // at SAVE time), or the page would show falsely "unsaved" on every open.
  useEffect(() => {
    if (scheduleQuery.data) setDraft((d) => d ?? scheduleQuery.data)
  }, [scheduleQuery.data])

  // Two independent dirty flags so each group's Save button lights up on its
  // own edits only. Comparing the picked slices means editing the digest never
  // marks the report reminders unsaved, and vice versa.
  const remindersDirty = useMemo(() => {
    if (!draft || !scheduleQuery.data) return false
    return (
      JSON.stringify(pickReminders(draft)) !==
      JSON.stringify(pickReminders(scheduleQuery.data))
    )
  }, [draft, scheduleQuery.data])
  const digestDirty = useMemo(() => {
    if (!draft || !scheduleQuery.data) return false
    return (
      JSON.stringify(pickDigest(draft)) !==
      JSON.stringify(pickDigest(scheduleQuery.data))
    )
  }, [draft, scheduleQuery.data])
  const repeatDirty = useMemo(() => {
    if (!draft || !scheduleQuery.data) return false
    return (
      JSON.stringify(pickRepeat(draft)) !==
      JSON.stringify(pickRepeat(scheduleQuery.data))
    )
  }, [draft, scheduleQuery.data])
  const creationDirty = useMemo(() => {
    if (!draft || !scheduleQuery.data) return false
    return (
      JSON.stringify(pickCreation(draft)) !==
      JSON.stringify(pickCreation(scheduleQuery.data))
    )
  }, [draft, scheduleQuery.data])
  const notificationDirty = useMemo(() => {
    if (!draft || !scheduleQuery.data) return false
    return (
      JSON.stringify(pickNotification(draft)) !==
      JSON.stringify(pickNotification(scheduleQuery.data))
    )
  }, [draft, scheduleQuery.data])
  // Cross-field rule (user-requested hard block): the Default Task Notification
  // Time must NOT be earlier than the Repeat Task Creation Time. When it is,
  // BOTH groups show a red error and their Save buttons are blocked (so it can't
  // be bypassed via the creation field either). HH:MM strings compare
  // lexicographically for zero-padded 24-hour times.
  const notifBeforeCreation = useMemo(() => {
    if (!draft) return false
    const creation = draft.repeatTaskCreationAt
    const notify = draft.defaultTaskNotificationAt
    return (
      typeof creation === 'string' &&
      typeof notify === 'string' &&
      notify < creation
    )
  }, [draft])

  // Report Reminders save — sends ONLY the reminder fields. On success it syncs
  // just those fields back into both the baseline (query cache) and the draft,
  // preserving any unsaved DIGEST edits.
  const saveReminders = useMutation({
    mutationFn: async (next) => {
      const res = await api.patch('/reports/config/schedule', {
        dailyEnabled: next.dailyEnabled,
        dailyReminderAt: next.dailyReminderAt,
        dailyActiveDays: next.dailyActiveDays,
        weeklyEnabled: next.weeklyEnabled,
        weeklyDayOfWeek: next.weeklyDayOfWeek,
        weeklyReminderAt: next.weeklyReminderAt,
        monthlyEnabled: next.monthlyEnabled,
        // Monthly cadence is fixed to "last day of month" (the 31 sentinel,
        // clamped to each month's real length by the runner) + "current
        // month". Enforced here at SAVE time only — the load path leaves the
        // saved value untouched so the page never falsely shows unsaved.
        monthlyDayOfMonth: 31,
        monthlyReminderAt: next.monthlyReminderAt,
        monthlyScope: 'current',
        dailyGraceEnabled: next.dailyGraceEnabled ?? true,
        weeklyAdminDigestEnabled: next.weeklyAdminDigestEnabled ?? true,
        monthlyAdminDigestEnabled: next.monthlyAdminDigestEnabled ?? true,
      })
      return reportSchedule.parse(res.data)
    },
    onSuccess: (saved) => {
      toast.success('Report reminders saved successfully.')
      qc.setQueryData(['report-schedule'], (old) => ({
        ...(old ?? saved),
        ...pickReminders(saved),
      }))
      setDraft((d) => ({ ...(d ?? saved), ...pickReminders(saved) }))
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  // Daily Task Assignment Digest save — sends ONLY the digest fields +
  // recipients, and syncs just those back, preserving unsaved REMINDER edits.
  const saveDigest = useMutation({
    mutationFn: async (next) => {
      // De-duplicate + sort the send times (the backend does the same; doing it
      // here keeps the optimistic draft in lockstep with what's stored).
      const times = [...new Set(next.taskDigestReminderTimes ?? [])].sort()
      const res = await api.patch('/reports/config/schedule', {
        taskDigestEnabled: next.taskDigestEnabled ?? false,
        taskDigestReminderAt: next.taskDigestReminderAt ?? '12:00',
        taskDigestReminderTimes: times.length
          ? times
          : [next.taskDigestReminderAt ?? '12:00'],
        taskDigestRecipients: (next.taskDigestRecipients ?? []).map((r) => ({
          id: r.id,
          fullName: r.fullName,
          email: r.email,
          kind: r.kind ?? 'to',
          status: r.status ?? 'active',
        })),
      })
      return reportSchedule.parse(res.data)
    },
    onSuccess: (saved) => {
      toast.success('Daily Task Assignment Digest saved successfully.')
      setShowRecipientErrors(false)
      qc.setQueryData(['report-schedule'], (old) => ({
        ...(old ?? saved),
        ...pickDigest(saved),
      }))
      setDraft((d) => ({ ...(d ?? saved), ...pickDigest(saved) }))
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  // Weekly Repeat Task Report save — sends ONLY the repeat-report fields +
  // recipients, syncing just those back so it never clobbers the reminder or
  // digest edits.
  const saveRepeat = useMutation({
    mutationFn: async (next) => {
      const res = await api.patch('/reports/config/schedule', {
        repeatReportEnabled: next.repeatReportEnabled ?? false,
        repeatReportDayOfWeek: next.repeatReportDayOfWeek ?? 1,
        repeatReportReminderAt: next.repeatReportReminderAt ?? '12:00',
        repeatReportRecipients: (next.repeatReportRecipients ?? []).map((r) => ({
          id: r.id,
          fullName: r.fullName,
          email: r.email,
          kind: r.kind ?? 'to',
          status: r.status ?? 'active',
        })),
      })
      return reportSchedule.parse(res.data)
    },
    onSuccess: (saved) => {
      toast.success('Repeat Task Summary saved successfully.')
      setShowRepeatRecipientErrors(false)
      qc.setQueryData(['report-schedule'], (old) => ({
        ...(old ?? saved),
        ...pickRepeat(saved),
      }))
      setDraft((d) => ({ ...(d ?? saved), ...pickRepeat(saved) }))
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  // Repeat Task Creation Time save — sends ONLY this one field, syncing just it
  // back so it never clobbers the reminder / digest / repeat-report edits.
  const saveCreation = useMutation({
    mutationFn: async (next) => {
      const res = await api.patch('/reports/config/schedule', {
        repeatTaskCreationAt: next.repeatTaskCreationAt ?? '00:00',
      })
      return reportSchedule.parse(res.data)
    },
    onSuccess: (saved) => {
      toast.success('Repeat Task Creation Time saved successfully.')
      qc.setQueryData(['report-schedule'], (old) => ({
        ...(old ?? saved),
        ...pickCreation(saved),
      }))
      setDraft((d) => ({ ...(d ?? saved), ...pickCreation(saved) }))
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  // Default Task Notification Time save — sends ONLY this one field, syncing just
  // it back so it never clobbers the reminder / digest / repeat-report / creation
  // edits. Separate from the Repeat Task Creation Time save above.
  const saveNotification = useMutation({
    mutationFn: async (next) => {
      const res = await api.patch('/reports/config/schedule', {
        defaultTaskNotificationAt: next.defaultTaskNotificationAt ?? '09:00',
      })
      return reportSchedule.parse(res.data)
    },
    onSuccess: (saved) => {
      toast.success('Default Task Notification Time saved successfully.')
      qc.setQueryData(['report-schedule'], (old) => ({
        ...(old ?? saved),
        ...pickNotification(saved),
      }))
      setDraft((d) => ({ ...(d ?? saved), ...pickNotification(saved) }))
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  // Restore one group's fields to the last-saved values, leaving the other
  // group's edits alone.
  function resetReminders() {
    if (scheduleQuery.data)
      setDraft((d) => ({ ...d, ...pickReminders(scheduleQuery.data) }))
  }
  function resetDigest() {
    if (scheduleQuery.data)
      setDraft((d) => ({ ...d, ...pickDigest(scheduleQuery.data) }))
    setShowRecipientErrors(false)
  }
  function resetRepeat() {
    if (scheduleQuery.data)
      setDraft((d) => ({ ...d, ...pickRepeat(scheduleQuery.data) }))
    setShowRepeatRecipientErrors(false)
  }
  function resetCreation() {
    if (scheduleQuery.data)
      setDraft((d) => ({ ...d, ...pickCreation(scheduleQuery.data) }))
  }
  function resetNotification() {
    if (scheduleQuery.data)
      setDraft((d) => ({ ...d, ...pickNotification(scheduleQuery.data) }))
  }

  // Gate the digest save on the Additional Recipient rows: every added row must
  // be complete (Full Name + valid Email) — surface the specific inline errors
  // + a summary toast instead of letting the backend reject with a generic
  // "Validation failed." that names no field, and never hit the API.
  function handleSaveDigest() {
    const rows = draft?.taskDigestRecipients ?? []
    const hasRowError = rows.some((r) => {
      const e = digestRecipientErrors(r)
      return e.fullName || e.email
    })
    if (hasRowError) {
      setShowRecipientErrors(true)
      toast.error('Complete or remove the highlighted additional recipients.')
      return
    }
    saveDigest.mutate(draft)
  }
  function handleSaveRepeat() {
    const rows = draft?.repeatReportRecipients ?? []
    const hasRowError = rows.some((r) => {
      const e = digestRecipientErrors(r)
      return e.fullName || e.email
    })
    if (hasRowError) {
      setShowRepeatRecipientErrors(true)
      toast.error('Complete or remove the highlighted additional recipients.')
      return
    }
    saveRepeat.mutate(draft)
  }

  if (scheduleQuery.isLoading || !draft) {
    return (
      <div className="p-8">
        <p className="text-sm text-[color:var(--color-ink-muted)]">
          Loading schedule…
        </p>
      </div>
    )
  }
  if (scheduleQuery.isError) {
    return (
      <div className="p-8">
        <p className="text-sm text-[color:var(--color-priority-urgent)]">
          {extractApiError(scheduleQuery.error).message}
        </p>
      </div>
    )
  }

  function patch(key, val) {
    setDraft((prev) => (prev ? { ...prev, [key]: val } : prev))
  }

  // Count enabled cards + work out which one fires next so the hero can
  // show "Next reminder · Tomorrow at 4:00 PM" without an extra request.
  const enabledCount =
    (draft.dailyEnabled ? 1 : 0) +
    (draft.weeklyEnabled ? 1 : 0) +
    (draft.monthlyEnabled ? 1 : 0)
  const nextReminder = computeNextOverall(draft)

  return (
    <div className="pb-12">
      <Breadcrumb current="Reminders & Schedule" />

      <div className="mb-6">
        <h1 className="font-display text-3xl text-[color:var(--color-ink-strong)] flex items-center gap-3">
          <CalendarClock
            size={20}
            className="text-[color:var(--color-accent-strong)]"
          />
          Reminders &amp; Schedule
        </h1>
        <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
          Set task reminders and when reports are due.
        </p>
      </div>

      {/* Hero — summary band: how many reminders are active + when the
       *  next one fires. Coloured amber when nothing is on so the admin
       *  spots the gap immediately. */}
      <section
        className={
          'mb-5 rounded-xl border p-4 flex items-center gap-4 ' +
          (enabledCount === 0
            ? 'bg-[color:var(--color-warning-bg)] border-[color:var(--color-warning-border)]'
            : 'bg-[color:var(--color-card)] border-[color:var(--color-rule)]')
        }
      >
        <span
          className={
            'h-12 w-12 rounded-xl flex items-center justify-center shrink-0 border ' +
            (enabledCount === 0
              ? 'bg-[color:var(--color-warning-bg)] text-[color:var(--color-warning)] border-[color:var(--color-warning-border)]'
              : 'bg-[#DBEAFE] text-[#2563EB] border-[#BFDBFE]')
          }
          aria-hidden="true"
        >
          <BellRing size={24} />
        </span>
        <div className="flex-1 min-w-0">
          <p
            className={
              'text-sm font-semibold ' +
              (enabledCount === 0
                ? 'text-[color:var(--color-warning)]'
                : 'text-[color:var(--color-ink-strong)]')
            }
          >
            {enabledCount === 0
              ? 'No reminders are active.'
              : `${enabledCount} of 3 reminder${enabledCount === 1 ? '' : 's'} active`}
          </p>
          <p className="text-xs text-[color:var(--color-ink-muted)] mt-0.5">
            {enabledCount === 0
              ? 'Turn at least one on so the team gets a reminder to submit reports.'
              : nextReminder
                ? `Next reminder · ${nextReminder.label} at ${formatTime(nextReminder.at)}`
                : 'Times below are in your company\'s local timezone.'}
          </p>
        </div>
      </section>

      {/* Task reminders — the company-wide "remind me before a task is
       *  due" ladder. Same shared editor + PATCH /admin/company endpoint
       *  as the Profile-page card; surfaced here too because it sits
       *  alongside the report reminders as a scheduling setting. Its own
       *  Save (independent of the report-schedule form below). */}
      <section className="mb-5 rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-5">
        <h2 className="font-display text-lg text-[color:var(--color-ink-strong)] font-semibold tracking-tight m-0 flex items-center gap-2">
          <BellRing size={18} className="text-[color:var(--color-accent-strong)]" />
          Task reminders
        </h2>
        <p className="mt-1 mb-3 text-sm text-[color:var(--color-ink-muted)]">
          Reminders sent to a task&apos;s assignees before its due time, and a
          repeating reminder while a task stays overdue. Applies to every task
          in the company. Reminders under a day only fire for tasks that have a
          specific due time.
        </p>
        <CompanyTaskRemindersEditor />

        {/* Repeat Task Creation Time — when the recurring-task runner GENERATES
         *  each due occurrence, in the company timezone. Distinct from the
         *  reminder ladder above (which only NOTIFIES about a task): this
         *  controls when a repeating task's next instance is created. Stored on
         *  the schedule (ReportSchedule) and saved independently. */}
        <div className="mt-5 pt-5 border-t border-[color:var(--color-rule)]">
          <TimeField
            label="Repeat Task Creation Time"
            value={draft.repeatTaskCreationAt}
            onChange={(v) => patch('repeatTaskCreationAt', v)}
            timezone={tenantTimezone}
          />
          <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
            Set the time when recurring tasks are automatically created for your
            company.
          </p>
          {/* Same cross-field rule, mirrored here so raising the creation time
           *  above the notification time is blocked too (no bypass). */}
          {notifBeforeCreation ? (
            <p className="mt-1.5 flex items-start gap-1.5 text-xs text-[color:var(--color-danger)]">
              <AlertCircle size={13} className="mt-0.5 shrink-0" />
              <span>
                The Repeat Task Creation Time can&rsquo;t be later than the
                Default Task Notification Time (
                {formatTime(draft.defaultTaskNotificationAt)}).
              </span>
            </p>
          ) : null}
          <SaveBar
            dirty={creationDirty}
            onReset={resetCreation}
            onSave={() => saveCreation.mutate(draft)}
            saving={saveCreation.isPending}
            blocked={notifBeforeCreation}
            label="Save Creation Time"
          />
        </div>

        {/* Default Task Notification Time — the FALLBACK time a task's assignment
         *  email is sent when the task has a start date but no specific start
         *  time (company timezone). Deliberately SEPARATE from the Repeat Task
         *  Creation Time above (which only controls WHEN recurring occurrences
         *  are generated) — the two are never mixed. Stored on the schedule and
         *  saved independently. */}
        <div className="mt-5 pt-5 border-t border-[color:var(--color-rule)]">
          <TimeField
            label="Default Task Notification Time"
            value={draft.defaultTaskNotificationAt}
            onChange={(v) => patch('defaultTaskNotificationAt', v)}
            timezone={tenantTimezone}
          />
          <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
            The time a recurring task&rsquo;s notification is sent when its
            occurrence has no specific start time. Other tasks notify at their
            own start time, or immediately if none.
          </p>
          {/* Hard validation (user-requested): the notification time must NOT be
           *  earlier than the Repeat Task Creation Time. When it is, show a red
           *  error and BLOCK the save (SaveBar `blocked`). */}
          {notifBeforeCreation ? (
            <p className="mt-1.5 flex items-start gap-1.5 text-xs text-[color:var(--color-danger)]">
              <AlertCircle size={13} className="mt-0.5 shrink-0" />
              <span>
                The Default Task Notification Time can&rsquo;t be earlier than the
                Repeat Task Creation Time (
                {formatTime(draft.repeatTaskCreationAt)}). Set it to{' '}
                {formatTime(draft.repeatTaskCreationAt)} or later.
              </span>
            </p>
          ) : null}
          <SaveBar
            dirty={notificationDirty}
            onReset={resetNotification}
            onSave={() => saveNotification.mutate(draft)}
            saving={saveNotification.isPending}
            blocked={notifBeforeCreation}
            label="Save Notification Time"
          />
        </div>
      </section>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-stretch">
        <ScheduleCard
          icon={Sun}
          tone="gold"
          title="Daily Report Reminder"
          subtitle="A single reminder each weekday."
          enabled={draft.dailyEnabled}
          onToggle={(v) => patch('dailyEnabled', v)}
          previewLine={
            draft.dailyEnabled
              ? `Fires ${describeActiveDays(draft.dailyActiveDays)} at ${formatTime(draft.dailyReminderAt)}`
              : null
          }
        >
          <div className="flex flex-col gap-3 h-full">
            {/* Selectable strip — pick which weekdays the daily reminder
             *  fires on. Constrained to keep at least one day on. */}
            <ActiveDaysPicker
              activeDays={draft.dailyActiveDays}
              onChange={(next) => patch('dailyActiveDays', next)}
              disabled={!draft.dailyEnabled}
            />
            {/* The active days double as the WORKING WEEK — they define
             *  the window the Weekly Report covers (first → last active
             *  day). Said out loud here because it's otherwise invisible:
             *  QA reported "configured Mon–Sat but the weekly report
             *  still shows Mon–Fri" after missing this link. */}
            <p className="text-[11px] text-[color:var(--color-ink-muted)] m-0">
              These days also set the working week your team's Weekly
              Report covers.
            </p>
            <TimeField
              label="Reminder at"
              value={draft.dailyReminderAt}
              onChange={(v) => patch('dailyReminderAt', v)}
              disabled={!draft.dailyEnabled}
              timezone={tenantTimezone}
            />
            {/* mt-auto pushes the escalation toggle to the bottom of
             *  the card so the three cards align on the same baseline
             *  regardless of how many fields the cadence has above it. */}
            <div className="mt-auto">
              <EscalationToggle
                label="Remind user 24 hours after a miss"
                hint="Send a friendly reminder."
                value={draft.dailyGraceEnabled ?? true}
                onChange={(v) => patch('dailyGraceEnabled', v)}
                disabled={!draft.dailyEnabled}
              />
            </div>
          </div>
        </ScheduleCard>

        <ScheduleCard
          icon={CalendarDays}
          tone="sky"
          title="Weekly Report Reminder"
          subtitle="Wraps up the working week."
          enabled={draft.weeklyEnabled}
          onToggle={(v) => patch('weeklyEnabled', v)}
          previewLine={
            draft.weeklyEnabled
              ? `Fires every ${WEEKDAYS[draft.weeklyDayOfWeek]} at ${formatTime(draft.weeklyReminderAt)} · covers ${describeWorkingWeek(draft.dailyActiveDays)}`
              : null
          }
        >
          <div className="flex flex-col gap-3 h-full">
            <WeekdayPicker
              label="Day of week"
              value={draft.weeklyDayOfWeek}
              onChange={(v) => patch('weeklyDayOfWeek', v)}
              disabled={!draft.weeklyEnabled}
            />
            <TimeField
              label="Reminder at"
              value={draft.weeklyReminderAt}
              onChange={(v) => patch('weeklyReminderAt', v)}
              disabled={!draft.weeklyEnabled}
              timezone={tenantTimezone}
            />
            <div className="mt-auto">
              <EscalationToggle
                label="Notify admins 48 hours after a miss"
                hint="Send a list of late users to admins."
                value={draft.weeklyAdminDigestEnabled ?? true}
                onChange={(v) => patch('weeklyAdminDigestEnabled', v)}
                disabled={!draft.weeklyEnabled}
              />
            </div>
          </div>
        </ScheduleCard>

        <ScheduleCard
          icon={CalendarRange}
          tone="emerald"
          title="Monthly Report Reminder"
          subtitle="A roll-up of the month's work."
          enabled={draft.monthlyEnabled}
          onToggle={(v) => patch('monthlyEnabled', v)}
          previewLine={
            draft.monthlyEnabled
              ? `Fires on the last day at ${formatTime(draft.monthlyReminderAt)}`
              : null
          }
        >
          <div className="flex flex-col gap-3 h-full">
            {/* The monthly cadence is FIXED: it always fires on the LAST DAY
             *  of the month and covers the CURRENT month. The specific-day
             *  picker and the "Last month" scope were removed per product
             *  decision (QA 2026-08-31); the values are pinned on load (see
             *  the prime effect). Only the reminder TIME stays editable. */}
            <FixedSettingRow
              label="Last day of month"
              hint="Always sends on the final day, whatever its length (28–31)."
            />
            <TimeField
              label="Reminder at"
              value={draft.monthlyReminderAt}
              onChange={(v) => patch('monthlyReminderAt', v)}
              disabled={!draft.monthlyEnabled}
              timezone={tenantTimezone}
            />
            <FixedSettingRow
              label="Current month"
              hint={`Covers the month in progress — shows "${formatPattern(new Date(), 'MMMM yyyy')}".`}
            />
            <div className="mt-auto">
              <EscalationToggle
                label="Notify admins 3 days after a miss"
                hint="Send a list of late users to admins."
                value={draft.monthlyAdminDigestEnabled ?? true}
                onChange={(v) => patch('monthlyAdminDigestEnabled', v)}
                disabled={!draft.monthlyEnabled}
              />
            </div>
          </div>
        </ScheduleCard>
      </div>

      {/* Report Reminders save — scoped to the daily / weekly / monthly cards
       *  above; independent of the digest's own Save below. */}
      <SaveBar
        dirty={remindersDirty}
        onReset={resetReminders}
        onSave={() => saveReminders.mutate(draft)}
        saving={saveReminders.isPending}
      />

      {/* Daily Task Assignment Digest — a leadership-facing roster of every
       *  task assigned to the team that day, sent to Company Admins (the whole
       *  company) and supervisors (their own team, by Work Role). Distinct
       *  from the report reminders above (which nudge report-writers), so it
       *  gets its own section AND its own Save button. */}
      <section className="mt-5 rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-5">
        <div className="flex items-start gap-3">
          <div
            className={
              'h-10 w-10 rounded-lg border flex items-center justify-center shrink-0 ' +
              TONE_TILE.sky +
              (draft.taskDigestEnabled ? '' : ' opacity-50')
            }
            aria-hidden="true"
          >
            <ClipboardList size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-display text-lg text-[color:var(--color-ink-strong)] font-semibold tracking-tight m-0">
              Daily Task Assignment Digest
            </h2>
            <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
              A once-a-day email listing every task assigned to the team so far
              that day — new assignments and reassignments. Company Admins
              receive the whole company; each supervisor receives the tasks
              assigned to the team members who report to them. Nothing is sent
              on a day with no assignments.
            </p>
          </div>
          <ToggleSwitch
            enabled={draft.taskDigestEnabled}
            onChange={(v) => patch('taskDigestEnabled', v)}
            srLabel="Daily Task Assignment Digest enabled"
          />
        </div>
        <div
          className={
            'mt-4 transition-opacity ' +
            (draft.taskDigestEnabled ? '' : 'opacity-50 pointer-events-none')
          }
        >
          {/* No width cap here: the picker is fixed-width dropdowns, so the
           *  only thing a cap squeezes is the timezone hint below it — which
           *  then wraps to three lines. Full width keeps the hint on one line. */}
          <DigestTimesEditor
            times={
              draft.taskDigestReminderTimes?.length
                ? draft.taskDigestReminderTimes
                : [draft.taskDigestReminderAt ?? '12:00']
            }
            onChange={(next) => patch('taskDigestReminderTimes', next)}
            disabled={!draft.taskDigestEnabled}
            timezone={tenantTimezone}
          />
          <DigestRecipientsEditor
            recipients={draft.taskDigestRecipients ?? []}
            onChange={(next) => patch('taskDigestRecipients', next)}
            disabled={!draft.taskDigestEnabled}
            showErrors={showRecipientErrors}
          />
        </div>
        {/* Digest save — kept OUTSIDE the disabled/opacity wrapper above so it
         *  stays clickable even while the digest is toggled off. */}
        <SaveBar
          dirty={digestDirty}
          onReset={resetDigest}
          onSave={handleSaveDigest}
          saving={saveDigest.isPending}
        />
      </section>

      {/* Weekly Repeat Task Report — a roster of every recurring task (config +
       *  status), grouped by department, with a Needs-Attention list. Distinct
       *  from the daily digest above (which reports the day's assignment
       *  activity), so it gets its own section, weekly schedule + Save button. */}
      <section className="mt-5 rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-5">
        <div className="flex items-start gap-3">
          <div
            className={
              'h-10 w-10 rounded-lg border flex items-center justify-center shrink-0 ' +
              TONE_TILE.sky +
              (draft.repeatReportEnabled ? '' : ' opacity-50')
            }
            aria-hidden="true"
          >
            <Repeat size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-display text-lg text-[color:var(--color-ink-strong)] font-semibold tracking-tight m-0">
              Repeat Task Summary
            </h2>
            <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
              A weekly summary of every recurring task — its schedule, next run
              and status — grouped by department, plus a Needs-Attention list
              (overdue, unassigned, paused or ending soon). Company Admins
              receive the whole company; each supervisor receives their own
              team's recurring tasks.
            </p>
          </div>
          <ToggleSwitch
            enabled={draft.repeatReportEnabled}
            onChange={(v) => patch('repeatReportEnabled', v)}
            srLabel="Repeat Task Summary enabled"
          />
        </div>
        <div
          className={
            'mt-4 transition-opacity ' +
            (draft.repeatReportEnabled ? '' : 'opacity-50 pointer-events-none')
          }
        >
          <div className="mb-3 text-sm text-[color:var(--color-ink-muted)]">
            Frequency:{' '}
            <span className="font-medium text-[color:var(--color-ink-strong)]">
              Weekly
            </span>
          </div>
          <div className="flex flex-col lg:flex-row lg:items-start gap-3">
            <WeekdayPicker
              label="Send on"
              className="lg:flex-1 lg:min-w-0"
              value={draft.repeatReportDayOfWeek}
              onChange={(v) => patch('repeatReportDayOfWeek', v)}
              disabled={!draft.repeatReportEnabled}
            />
            <TimeField
              label="Send at"
              value={draft.repeatReportReminderAt}
              onChange={(v) => patch('repeatReportReminderAt', v)}
              disabled={!draft.repeatReportEnabled}
              timezone={tenantTimezone}
            />
          </div>
          <DigestRecipientsEditor
            recipients={draft.repeatReportRecipients ?? []}
            onChange={(next) => patch('repeatReportRecipients', next)}
            disabled={!draft.repeatReportEnabled}
            showErrors={showRepeatRecipientErrors}
          />
        </div>
        <SaveBar
          dirty={repeatDirty}
          onReset={resetRepeat}
          onSave={handleSaveRepeat}
          saving={saveRepeat.isPending}
        />
      </section>
    </div>
  )
}

/** Inline per-section save bar. Appears only when its group has unsaved edits;
 *  each schedule group (report reminders, digest) renders its own so the two
 *  save independently. */
function SaveBar({ dirty, onReset, onSave, saving, label = 'Save Changes', blocked = false }) {
  if (!dirty) return null
  return (
    <div className="mt-4 flex items-center justify-end gap-3">
      <span className="mr-auto inline-flex items-center gap-1.5 text-sm font-medium text-[color:var(--color-warning)]">
        <CircleDot size={12} />
        Unsaved changes
      </span>
      <button
        type="button"
        onClick={onReset}
        disabled={saving}
        title="Reset to last saved"
        className="inline-flex items-center gap-1.5 px-3 h-9 rounded-full text-sm font-medium text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <RotateCcw size={13} />
        Reset
      </button>
      <button
        type="button"
        onClick={onSave}
        // `blocked` gates the Save on a cross-field validation error (e.g. the
        // notification time being earlier than the creation time). Reset stays
        // enabled so the user can undo the invalid edit.
        disabled={saving || blocked}
        title={blocked ? 'Fix the highlighted error before saving.' : undefined}
        className="inline-flex items-center gap-2 h-9 px-4 rounded-full text-sm font-semibold bg-[color:var(--color-primary)] text-white hover:bg-[color:var(--color-primary-hover)] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Save size={13} />
        {saving ? 'Saving…' : label}
      </button>
    </div>
  )
}

// ----------------------------------------------------------------------
// Card — section header (icon tile + title + toggle) + body + footer.
// ----------------------------------------------------------------------

function ScheduleCard({
  icon: Icon,
  tone,
  title,
  subtitle,
  enabled,
  onToggle,
  children,
  footer,
  previewLine,
}) {
  return (
    <section
      className={
        'rounded-xl border bg-[color:var(--color-card)] transition-all flex flex-col h-full ' +
        (enabled
          ? 'border-[color:var(--color-rule)] shadow-sm'
          : 'border-[color:var(--color-rule)]/70 bg-[color:var(--color-canvas)]/60')
      }
    >
      {/* Header — icon tile + title + toggle */}
      <div className="flex items-start gap-3 p-4 border-b border-[color:var(--color-rule)]/60">
        <div
          className={
            'h-10 w-10 rounded-lg border flex items-center justify-center shrink-0 ' +
            TONE_TILE[tone] +
            (enabled ? '' : ' opacity-50')
          }
          aria-hidden="true"
        >
          <Icon size={18} />
        </div>
        {/* Fixed-height header block — reserves space for a 2-line title,
         *  the subtitle, and the preview line so every card's body
         *  starts at the same Y across the row. */}
        <div className="flex-1 min-w-0 min-h-[5.5rem]">
          <p
            className={
              'text-sm font-semibold leading-snug ' +
              (enabled
                ? 'text-[color:var(--color-ink-strong)]'
                : 'text-[color:var(--color-ink-muted)]')
            }
          >
            {title}
          </p>
          <p className="text-xs text-[color:var(--color-ink-muted)] mt-0.5">
            {subtitle}
          </p>
          <p className="mt-1.5 text-xs text-[color:var(--color-ink-strong)] font-medium tabular-nums">
            {previewLine || ' '}
          </p>
        </div>
        <ToggleSwitch
          enabled={enabled}
          onChange={onToggle}
          srLabel={`${title} enabled`}
        />
      </div>

      {/* Body — fields, dimmed when disabled. flex-1 stretches to fill so
       *  the footer always sits flush at the bottom of the card. */}
      <div
        className={
          'p-4 flex-1 transition-opacity ' +
          (enabled ? '' : 'opacity-50 pointer-events-none')
        }
      >
        {children}
      </div>

      {/* Footer — helper text, anchored to the card bottom. */}
      {footer ? (
        <div className="px-4 pb-3 -mt-1 mt-auto">
          <p className="text-sm text-[color:var(--color-ink-muted)] leading-relaxed">
            {footer}
          </p>
        </div>
      ) : null}
    </section>
  )
}

// ----------------------------------------------------------------------
// Toggle switch — visual replacement for the bare checkbox.
// ----------------------------------------------------------------------

function ToggleSwitch({
  enabled,
  onChange,
  srLabel,
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      aria-label={srLabel}
      onClick={() => onChange(!enabled)}
      className={
        'relative inline-flex shrink-0 h-6 w-11 rounded-full transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-accent)]/40 ' +
        (enabled
          ? 'bg-[color:var(--color-accent)]'
          : 'bg-[color:var(--color-rule-strong)]')
      }
    >
      <span
        aria-hidden="true"
        className={
          'absolute top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform ' +
          (enabled ? 'translate-x-5' : 'translate-x-0.5')
        }
      />
    </button>
  )
}

// ----------------------------------------------------------------------
// Field primitives.
// ----------------------------------------------------------------------

function FieldLabel({ children }) {
  return (
    <label className="block text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] mb-1.5">
      {children}
    </label>
  )
}

const INPUT_BASE =
  'w-full h-10 px-3 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] text-sm text-[color:var(--color-ink)] outline-none transition-colors focus:border-[color:var(--color-accent)] focus:ring-2 focus:ring-[color:var(--color-accent)]/20 disabled:opacity-60 disabled:cursor-not-allowed'

/** Interactive weekday strip used inside the Daily card. Each pill is
 *  a toggle for whether the daily reminder fires on that weekday. The
 *  picker refuses to deselect the last remaining day, since an empty
 *  selection would mean the reminder never fires — that's what the
 *  whole-card On/Off toggle is for. */
function ActiveDaysPicker({ activeDays, onChange, disabled }) {
  const set = new Set(activeDays)
  function toggle(i) {
    if (disabled) return
    const next = new Set(set)
    if (next.has(i)) {
      // Block the last-day case so the picker never reaches "fires never".
      if (next.size === 1) {
        toast.warning('Pick at least one day, or turn the daily reminder off.')
        return
      }
      next.delete(i)
    } else {
      next.add(i)
    }
    onChange(Array.from(next).sort((a, b) => a - b))
  }
  return (
    <div className={disabled ? 'opacity-60' : ''}>
      <FieldLabel>Active days</FieldLabel>
      <div
        role="group"
        aria-label="Active days"
        aria-disabled={disabled}
        className={'grid grid-cols-7 gap-1 ' + (disabled ? 'pointer-events-none' : '')}
      >
        {WEEKDAYS_SHORT.map((d, i) => {
          const active = set.has(i)
          return (
            <button
              key={d}
              type="button"
              role="checkbox"
              aria-checked={active}
              aria-label={WEEKDAYS[i]}
              onClick={() => toggle(i)}
              className={
                'h-10 rounded-md text-xs font-semibold transition-colors cursor-pointer border ' +
                (active
                  ? 'bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)] border-[color:var(--color-accent-border)]'
                  : 'bg-[color:var(--color-card)] text-[color:var(--color-ink-muted)] border-[color:var(--color-rule)] hover:bg-[color:var(--color-card-hover)] hover:text-[color:var(--color-ink)]')
              }
            >
              {d}
            </button>
          )
        })}
      </div>
    </div>
  )
}

// Time picker building blocks. We render app-styled hour / minute / AM-PM
// dropdowns (matching DateTimePickerField's time controls) instead of the
// native `<input type="time">`, whose picker is a browser-themed widget that
// looks out of place against the rest of the app. The stored value stays a
// 24-hour "HH:MM" string, so the backend contract is unchanged.
const TIME_HOURS_12 = Array.from({ length: 12 }, (_, i) => i + 1)
const TIME_MINUTES = Array.from({ length: 60 }, (_, i) => i)
const TIME_SELECT =
  'h-10 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] text-sm text-[color:var(--color-ink)] px-2 cursor-pointer outline-none transition-colors focus:border-[color:var(--color-accent)] focus:ring-2 focus:ring-[color:var(--color-accent)]/20 disabled:opacity-60 disabled:cursor-not-allowed'

/** Split a 24-hour "HH:MM" string into 12-hour parts for the dropdowns. */
function parseHHMM(value) {
  const m = /^(\d{1,2}):(\d{2})$/.exec(value ?? '')
  const hour = m ? Math.min(23, Number(m[1])) : 0
  const minute = m ? Math.min(59, Number(m[2])) : 0
  return {
    minute,
    hour12: ((hour + 11) % 12) + 1,
    meridiem: hour < 12 ? 'AM' : 'PM',
  }
}
/** Recombine 12-hour parts back into the stored 24-hour "HH:MM" string. */
function toHHMM(hour12, minute, meridiem) {
  const h24 = meridiem === 'PM' ? (hour12 % 12) + 12 : hour12 % 12
  return `${String(h24).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
}

/** "Interpreted as Asia/Kolkata (your company timezone)" under a time
 *  picker, with a link to where the company timezone is set (My Preferences →
 *  Region & formats). */
function CompanyTimezoneHint({ timezone, className }) {
  const role = useAuthStore((s) => s.user?.role)
  return (
    <p className={`${className} text-xs text-[color:var(--color-ink-muted)]`}>
      Interpreted as <span className="font-medium">{timezone}</span> (your company
      timezone). If it&rsquo;s wrong, change it in{' '}
      <Link
        to={`${preferencesPathFor(role)}#company`}
        className="font-medium text-[color:var(--color-accent-dim)] underline-offset-2 hover:underline"
      >
        My Preferences
      </Link>
      .
    </p>
  )
}

function TimeField({
  label,
  value,
  onChange,
  disabled,
  timezone,
}) {
  const { hour12, minute, meridiem } = parseHHMM(value)
  return (
    <div>
      <FieldLabel>{label}</FieldLabel>
      <div className="flex items-center gap-1.5">
        <select
          aria-label={`${label} — hour`}
          value={hour12}
          onChange={(e) => onChange(toHHMM(Number(e.target.value), minute, meridiem))}
          disabled={disabled}
          className={TIME_SELECT}
        >
          {TIME_HOURS_12.map((h) => (
            <option key={h} value={h}>
              {String(h).padStart(2, '0')}
            </option>
          ))}
        </select>
        <span className="text-sm text-[color:var(--color-ink-muted)]">:</span>
        <select
          aria-label={`${label} — minute`}
          value={minute}
          onChange={(e) => onChange(toHHMM(hour12, Number(e.target.value), meridiem))}
          disabled={disabled}
          className={TIME_SELECT}
        >
          {TIME_MINUTES.map((m) => (
            <option key={m} value={m}>
              {String(m).padStart(2, '0')}
            </option>
          ))}
        </select>
        <select
          aria-label={`${label} — AM or PM`}
          value={meridiem}
          onChange={(e) => onChange(toHHMM(hour12, minute, e.target.value))}
          disabled={disabled}
          className={TIME_SELECT}
        >
          <option value="AM">AM</option>
          <option value="PM">PM</option>
        </select>
      </div>
      {timezone ? (
        <CompanyTimezoneHint timezone={timezone} className="mt-1" />
      ) : null}
    </div>
  )
}

/** Multiple daily send times for the Daily Task Assignment Digest. Add / edit /
 *  remove rows; the list stays sorted + unique (duplicate times are blocked).
 *  Each row is the same hour / minute / AM-PM picker as TimeField; the timezone
 *  hint shows once below the list. At least one time is always kept. */
function DigestTimesEditor({ times, onChange, disabled, timezone }) {
  const list = [...new Set(times ?? [])].sort()
  const [dupError, setDupError] = useState(false)
  const commit = (next) => onChange([...new Set(next)].sort())

  const editAt = (idx, value) => {
    // Block duplicates — if the picked time already exists on another row, flag
    // it and leave the row where it was (the controlled selects don't move).
    if (list.some((t, i) => i !== idx && t === value)) {
      setDupError(true)
      return
    }
    setDupError(false)
    const next = list.slice()
    next[idx] = value
    commit(next)
  }

  const removeAt = (idx) => {
    if (list.length <= 1) return // always keep at least one send time
    setDupError(false)
    commit(list.filter((_, i) => i !== idx))
  }

  const add = () => {
    // Add the first on-the-hour slot not already used, so adding never dupes.
    const used = new Set(list)
    let candidate = '12:00'
    for (let h = 9; h < 9 + 24; h += 1) {
      const t = `${String(h % 24).padStart(2, '0')}:00`
      if (!used.has(t)) {
        candidate = t
        break
      }
    }
    setDupError(false)
    commit([...list, candidate])
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <FieldLabel>Send at</FieldLabel>
        <button
          type="button"
          onClick={add}
          disabled={disabled}
          className="inline-flex items-center gap-1 text-xs font-medium text-[color:var(--color-accent-strong)] hover:underline disabled:opacity-50 cursor-pointer"
        >
          <Plus size={13} /> Add time
        </button>
      </div>
      <div className="flex flex-col gap-2">
        {list.map((t, idx) => {
          const { hour12, minute, meridiem } = parseHHMM(t)
          return (
            <div key={t} className="flex items-center gap-1.5">
              <select
                aria-label={`Send time ${idx + 1} — hour`}
                value={hour12}
                disabled={disabled}
                onChange={(e) => editAt(idx, toHHMM(Number(e.target.value), minute, meridiem))}
                className={TIME_SELECT}
              >
                {TIME_HOURS_12.map((h) => (
                  <option key={h} value={h}>
                    {String(h).padStart(2, '0')}
                  </option>
                ))}
              </select>
              <span className="text-sm text-[color:var(--color-ink-muted)]">:</span>
              <select
                aria-label={`Send time ${idx + 1} — minute`}
                value={minute}
                disabled={disabled}
                onChange={(e) => editAt(idx, toHHMM(hour12, Number(e.target.value), meridiem))}
                className={TIME_SELECT}
              >
                {TIME_MINUTES.map((m) => (
                  <option key={m} value={m}>
                    {String(m).padStart(2, '0')}
                  </option>
                ))}
              </select>
              <select
                aria-label={`Send time ${idx + 1} — AM or PM`}
                value={meridiem}
                disabled={disabled}
                onChange={(e) => editAt(idx, toHHMM(hour12, minute, e.target.value))}
                className={TIME_SELECT}
              >
                <option value="AM">AM</option>
                <option value="PM">PM</option>
              </select>
              <button
                type="button"
                onClick={() => removeAt(idx)}
                disabled={disabled || list.length <= 1}
                aria-label={`Remove send time ${idx + 1}`}
                title={list.length <= 1 ? 'Keep at least one send time' : 'Remove this time'}
                className="ml-1 inline-flex items-center justify-center h-8 w-8 rounded-md text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-priority-urgent)] hover:bg-[color:var(--color-canvas)] disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
              >
                <Trash2 size={15} />
              </button>
            </div>
          )
        })}
      </div>
      {dupError ? (
        <p className="mt-1 text-xs text-[color:var(--color-priority-urgent)]">
          That time is already added.
        </p>
      ) : null}
      {timezone ? (
        <CompanyTimezoneHint timezone={timezone} className="mt-1.5" />
      ) : null}
    </div>
  )
}

/** Day-of-week selector rendered as 7 toggle pills instead of a select.
 *  Faster to scan (you can see all options), and lines up with the
 *  weekly cadence concept better than a dropdown. */
/** Compact on/off switch for an escalation flow. Two-line label
 *  (action + one-line hint) so the admin can tell at a glance what
 *  flipping the toggle changes. Mirrors the chrome of `ScheduleCard`'s
 *  top-level toggle but at a smaller scale because it's nested. */
function EscalationToggle({ label, hint, value, onChange, disabled }) {
  return (
    <label
      className={
        'flex items-start justify-between gap-3 rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] px-3 py-2 ' +
        (disabled ? 'opacity-60 pointer-events-none' : 'cursor-pointer hover:bg-[color:var(--color-card-hover)]')
      }
    >
      <span className="min-w-0">
        <span className="block text-sm font-medium text-[color:var(--color-ink-strong)]">
          {label}
        </span>
        <span className="block text-xs text-[color:var(--color-ink-muted)] mt-0.5">
          {hint}
        </span>
      </span>
      <span className="relative inline-flex items-center shrink-0">
        <input
          type="checkbox"
          checked={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
          className="sr-only peer"
        />
        <span className="w-9 h-5 rounded-full bg-[color:var(--color-rule)] peer-checked:bg-[color:var(--color-accent)] transition-colors" />
        <span className="absolute left-0.5 inline-block h-4 w-4 rounded-full bg-white shadow transition-transform peer-checked:translate-x-4" />
      </span>
    </label>
  )
}

/** A read-only "this setting is fixed on" row — same footprint as an
 *  EscalationToggle but non-interactive. Used for the monthly cadence
 *  settings that are no longer admin-configurable: it always fires on the
 *  last day of the month and always covers the current month. */
function FixedSettingRow({ label, hint }) {
  return (
    <div className="rounded-lg border border-[color:var(--color-accent-border)] bg-[color:var(--color-accent-bg)] px-3 py-2">
      <span className="block text-sm font-medium text-[color:var(--color-accent-strong)]">
        {label}
      </span>
      <span className="block text-xs text-[color:var(--color-ink-muted)] mt-0.5">
        {hint}
      </span>
    </div>
  )
}

// Additional-recipient fields reuse the EXACT sign-up validators so the rules
// and wording match everywhere: Full Name (2–50 chars, letters/spaces/'-.) and
// a strict Email ID (no spaces, no consecutive dots, strict TLD, ≤100).
const RECIPIENT_NAME_SCHEMA = nameSchema('Full Name')
const RECIPIENT_EMAIL_SCHEMA = emailSchema({ strictTld: true })

/** Per-row validation for an Additional Recipient. Every row the admin has
 *  added must be complete — Full Name and Email ID are BOTH required (they
 *  carry a red asterisk), so an EMPTY row reports "… is required." on save
 *  rather than being silently dropped. To have no recipient, remove the row
 *  with its delete button. Uses the sign-up schemas so the wording matches the
 *  rest of the app. */
function digestRecipientErrors(r) {
  const errors = {}
  const nameRes = RECIPIENT_NAME_SCHEMA.safeParse(r.fullName ?? '')
  if (!nameRes.success) errors.fullName = nameRes.error.issues[0]?.message
  const emailRes = RECIPIENT_EMAIL_SCHEMA.safeParse(r.email ?? '')
  if (!emailRes.success) errors.email = emailRes.error.issues[0]?.message
  return errors
}

/** Company-Admin editor for the digest's explicit To / CC / BCC recipients.
 *  These people receive the COMPANY-WIDE digest in addition to the automatic
 *  Company Admin + supervisor sends; CC/BCC are copied on that email. */
function DigestRecipientsEditor({ recipients, onChange, disabled, showErrors }) {
  const rows = recipients ?? []
  const update = (id, patch) =>
    onChange(rows.map((r) => (r.id === id ? { ...r, ...patch } : r)))
  const remove = (id) => onChange(rows.filter((r) => r.id !== id))
  const add = () =>
    onChange([
      ...rows,
      { id: crypto.randomUUID(), fullName: '', email: '', kind: 'to', status: 'active' },
    ])

  const activeWith = (pred) =>
    rows.filter((r) => r.status !== 'inactive' && r.email.trim() && pred(r))
  const needsTo =
    activeWith((r) => r.kind === 'cc' || r.kind === 'bcc').length > 0 &&
    activeWith((r) => r.kind === 'to').length === 0

  return (
    <div className="mt-5">
      <div className="flex items-center justify-between mb-1.5">
        <FieldLabel>Additional recipients</FieldLabel>
        <button
          type="button"
          onClick={add}
          disabled={disabled}
          className="inline-flex items-center gap-1 text-xs font-medium text-[color:var(--color-accent-strong)] hover:underline disabled:opacity-50 cursor-pointer"
        >
          <Plus size={13} /> Add Recipient
        </button>
      </div>
      <p className="text-[11px] text-[color:var(--color-ink-muted)] m-0 mb-2">
        These people also receive the company-wide digest, on top of the Company
        Admin and supervisors. CC and BCC are copied on that email.
      </p>
      {rows.length === 0 ? (
        <p className="text-xs text-[color:var(--color-ink-muted)] italic">
          No additional recipients.
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          {/* Column headers — act as the field labels for the repeating rows.
           *  Full Name and Email ID are both required (red asterisk, no space
           *  before it per the field-label standard). */}
          <div className="flex items-end gap-2">
            <span className="flex-1 min-w-0 text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)]">
              Full Name
              <span className="text-[color:var(--color-priority-urgent)]">*</span>
            </span>
            <span className="flex-1 min-w-0 text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)]">
              Email ID
              <span className="text-[color:var(--color-priority-urgent)]">*</span>
            </span>
            <span className="w-24 shrink-0 text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)]">
              Type
            </span>
            <span className="w-10 shrink-0" aria-hidden="true" />
          </div>
          {rows.map((r) => {
            const err = digestRecipientErrors(r)
            // Errors surface ONLY after a Save Changes attempt (`showErrors`) —
            // nothing is flagged while the row is being typed. Clicking Save
            // reveals every problem (required + format) at once.
            const emailError = showErrors ? err.email : null
            const nameError = showErrors ? err.fullName : null
            return (
              <div key={r.id} className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <input
                    type="text"
                    value={r.fullName}
                    placeholder="Enter the Full Name"
                    aria-label="Full Name"
                    onChange={(e) => update(r.id, { fullName: e.target.value })}
                    disabled={disabled}
                    className={
                      INPUT_BASE +
                      (nameError
                        ? ' border-[color:var(--color-priority-urgent)]'
                        : '')
                    }
                  />
                  {nameError ? (
                    <p className="mt-1 text-[11px] text-[color:var(--color-priority-urgent)]">
                      {nameError}
                    </p>
                  ) : null}
                </div>
                <div className="flex-1 min-w-0">
                  <input
                    type="email"
                    value={r.email}
                    placeholder="Enter the Email ID"
                    aria-label="Email ID"
                    onChange={(e) => update(r.id, { email: e.target.value })}
                    disabled={disabled}
                    className={
                      INPUT_BASE +
                      (emailError
                        ? ' border-[color:var(--color-priority-urgent)]'
                        : '')
                    }
                  />
                  {emailError ? (
                    <p className="mt-1 text-[11px] text-[color:var(--color-priority-urgent)]">
                      {emailError}
                    </p>
                  ) : null}
                </div>
                <select
                  value={r.kind}
                  onChange={(e) => update(r.id, { kind: e.target.value })}
                  disabled={disabled}
                  aria-label="Recipient type"
                  className={INPUT_BASE.replace('w-full', 'w-24 shrink-0')}
                >
                  <option value="to">To</option>
                  <option value="cc">CC</option>
                  <option value="bcc">BCC</option>
                </select>
                <button
                  type="button"
                  onClick={() => remove(r.id)}
                  disabled={disabled}
                  title="Remove recipient"
                  className="h-10 w-10 shrink-0 inline-flex items-center justify-center rounded-md text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-priority-urgent)] hover:bg-[color:var(--color-card-hover)] disabled:opacity-50 cursor-pointer"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            )
          })}
        </div>
      )}
      {needsTo ? (
        <p className="mt-2 text-[11px] text-[color:var(--color-warning)]">
          Add at least one “To” recipient — CC/BCC are only sent alongside a To
          recipient.
        </p>
      ) : null}
    </div>
  )
}

function WeekdayPicker({ label, value, onChange, disabled, className = '' }) {
  return (
    <div className={className}>
      <FieldLabel>{label}</FieldLabel>
      <div
        role="radiogroup"
        aria-label={label}
        aria-disabled={disabled}
        className={
          // Seven equal columns that fill the available width, but each is held
          // to a >=2.25rem minimum so the labels always keep breathing room from
          // their borders. If the container is ever narrower than the seven
          // minimums (very small screens), the strip scrolls horizontally rather
          // than squeezing the text — it never collapses or wraps a lone day.
          'grid grid-cols-[repeat(7,minmax(40px,1fr))] gap-1 overflow-x-auto ' +
          (disabled ? 'opacity-60 pointer-events-none' : '')
        }
      >
        {WEEKDAYS_SHORT.map((d, i) => {
          const selected = i === value
          return (
            <button
              key={d}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => onChange(i)}
              className={
                'h-10 px-1 rounded-md text-xs font-semibold transition-colors cursor-pointer border ' +
                (selected
                  ? 'bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)] border-[color:var(--color-accent-border)]'
                  : 'bg-[color:var(--color-card)] text-[color:var(--color-ink-muted)] border-[color:var(--color-rule)] hover:bg-[color:var(--color-card-hover)] hover:text-[color:var(--color-ink)]')
              }
            >
              {d}
            </button>
          )
        })}
      </div>
    </div>
  )
}

// ----------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------

/** Format a "HH:mm" 24h string as 12h with am/pm — matches what the
 *  rest of the app uses for timestamps. */
function formatTime(hhmm) {
  if (!hhmm || typeof hhmm !== 'string') return '—'
  const [h, m] = hhmm.split(':').map(Number)
  if (!Number.isFinite(h) || !Number.isFinite(m)) return hhmm
  // The person's 24-hour clock (My Preferences) shows the stored value as-is.
  if (uses24HourClock()) return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
  const d = new Date()
  d.setHours(h, m, 0, 0)
  const raw = d.toLocaleTimeString('en-GB', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
  // `en-GB` outputs lowercase "am / pm" — normalise to uppercase so
  // the output matches `formatTime()` in `lib/format.js` exactly.
  return raw.replace(/\b(am|pm)\b/i, (m) => m.toUpperCase())
}


/** Human-readable summary of which days the daily reminder fires on.
 *  Recognises the common Mon–Fri / weekend / every-day cases so the
 *  preview line stays terse. */
function describeActiveDays(days) {
  if (!Array.isArray(days) || days.length === 0) return 'never'
  const set = new Set(days)
  const isWeekday = (i) => i >= 1 && i <= 5
  if (days.length === 7) return 'every day'
  if (days.length === 5 && [1, 2, 3, 4, 5].every((i) => set.has(i))) {
    return 'every weekday'
  }
  if (days.length === 2 && set.has(0) && set.has(6)) return 'every weekend'
  if (days.every(isWeekday)) {
    // Subset of weekdays — list them.
    return 'every ' + days.map((i) => WEEKDAYS_SHORT[i]).join(', ')
  }
  return 'every ' + days.map((i) => WEEKDAYS_SHORT[i]).join(', ')
}

/** "Mon → Sat" — the working-week window the Weekly Report covers,
 *  derived from the daily active days exactly like the report pages do
 *  (first → last active weekday of the Monday-anchored week). Shown on
 *  the Weekly card so an admin editing the active days can SEE the
 *  weekly window move with them. */
function describeWorkingWeek(days) {
  const list =
    Array.isArray(days) && days.length > 0 ? days : [1, 2, 3, 4, 5]
  // Monday-anchored ordering (Mon=0..Sun=6) so Sunday sorts last, matching
  // the Mon-anchored week the period math walks.
  const sorted = [...new Set(list)].sort((a, b) => ((a + 6) % 7) - ((b + 6) % 7))
  const first = sorted[0]
  const last = sorted[sorted.length - 1]
  if (first === last) return WEEKDAYS_SHORT[first]
  return `${WEEKDAYS_SHORT[first]} → ${WEEKDAYS_SHORT[last]}`
}

/** Find the soonest reminder across all enabled cards.
 *  Returns `{ label, at }` where `at` is the "HH:mm" string. */
function computeNextOverall(draft) {
  const now = new Date()
  const candidates = []
  if (draft.dailyEnabled && draft.dailyReminderAt) {
    const next = nextDaily(now, draft.dailyReminderAt, draft.dailyActiveDays)
    if (next) candidates.push({ ...next, at: draft.dailyReminderAt })
  }
  if (draft.weeklyEnabled && draft.weeklyReminderAt != null) {
    candidates.push({
      ...nextWeekly(now, draft.weeklyDayOfWeek, draft.weeklyReminderAt),
      at: draft.weeklyReminderAt,
    })
  }
  if (draft.monthlyEnabled && draft.monthlyReminderAt != null) {
    candidates.push({
      ...nextMonthly(now, draft.monthlyDayOfMonth, draft.monthlyReminderAt),
      at: draft.monthlyReminderAt,
    })
  }
  if (candidates.length === 0) return null
  candidates.sort((a, b) => a.fireAt - b.fireAt)
  return candidates[0]
}

function setHHMM(date, hhmm) {
  const [h, m] = hhmm.split(':').map(Number)
  const d = new Date(date)
  d.setHours(h ?? 0, m ?? 0, 0, 0)
  return d
}

function nextDaily(now, hhmm, activeDays) {
  // Walk forward day by day until the next active weekday whose hh:mm
  // is still in the future. The 8-day cap covers the worst case
  // (only one day a week selected and today's slot has already passed).
  if (!Array.isArray(activeDays) || activeDays.length === 0) return null
  const set = new Set(activeDays)
  let cursor = new Date(now)
  for (let i = 0; i < 8; i++) {
    const candidate = setHHMM(cursor, hhmm)
    const dow = candidate.getDay()
    if (set.has(dow) && candidate > now) {
      return { label: humanLabelForDate(candidate, now), fireAt: candidate }
    }
    cursor = new Date(cursor.getTime() + 24 * 60 * 60 * 1000)
  }
  // Fallback — shouldn't happen for a daily.
  return { label: 'Soon', fireAt: setHHMM(cursor, hhmm) }
}

function nextWeekly(now, dayOfWeek, hhmm) {
  const cursor = new Date(now)
  const today = cursor.getDay()
  let delta = (dayOfWeek - today + 7) % 7
  let candidate = setHHMM(
    new Date(cursor.getTime() + delta * 24 * 60 * 60 * 1000),
    hhmm,
  )
  if (candidate <= now) {
    delta += 7
    candidate = setHHMM(
      new Date(cursor.getTime() + delta * 24 * 60 * 60 * 1000),
      hhmm,
    )
  }
  return { label: humanLabelForDate(candidate, now), fireAt: candidate }
}

function nextMonthly(now, dayOfMonth, hhmm) {
  // Clamp the configured day to each candidate month's real length so the
  // "Last day of month" sentinel (31) — and any day beyond a short month's
  // end — lands on that month's actual last day instead of spilling into
  // the next month.
  const build = (year, monthIdx) => {
    const lastDay = new Date(year, monthIdx + 1, 0).getDate()
    return setHHMM(new Date(year, monthIdx, Math.min(dayOfMonth, lastDay)), hhmm)
  }
  const thisMonth = build(now.getFullYear(), now.getMonth())
  if (thisMonth > now) {
    return { label: humanLabelForDate(thisMonth, now), fireAt: thisMonth }
  }
  // Already past this month — roll into next month.
  const fire = build(now.getFullYear(), now.getMonth() + 1)
  return { label: humanLabelForDate(fire, now), fireAt: fire }
}

function humanLabelForDate(d, now) {
  const ms = d.getTime() - now.getTime()
  const days = Math.round(ms / (24 * 60 * 60 * 1000))
  if (days <= 0) return 'Today'
  if (days === 1) return 'Tomorrow'
  if (days < 7) {
    return d.toLocaleDateString('en-GB', { weekday: 'long' })
  }
  return d.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
  })
}
